/**
 * `elyra doctor models` — live-verify configured providers against the
 * model registry without starting an agent session. Uses the same probe
 * engine as the doctor extension's probe_models tool, with credentials
 * resolved from auth.json and environment variables.
 */

import {
	formatProbeReport,
	getEnvApiKey,
	getModels,
	getProviders,
	type KnownProvider,
	runModelProbes,
} from "@elyracode/ai";
import chalk from "chalk";
import { APP_NAME, getRoutingLedgerPath } from "../config.js";
import { AuthStorage } from "../core/auth-storage.js";
import { execCommand } from "../core/exec.js";
import { checkDefaults } from "../core/model-freshness.js";
import { defaultModelPerProvider } from "../core/model-resolver.js";
import { findElyraInstances, formatResourceReport, parsePsOutput } from "../core/resource-inspector.js";
import { formatStatsReport, loadStats } from "../core/routing-ledger.js";
import {
	createCostLookup,
	formatRecommendations,
	recommendTierModels,
	toModelCostInfo,
} from "../core/routing-recommendations.js";
import { SettingsManager } from "../core/settings-manager.js";

function printDoctorHelp(): void {
	console.log(`Usage: ${APP_NAME} doctor <command> [options]

Commands:
  preflight           Validate configured defaults against the model registry
                      (offline) and report newer models where available.
  models              Live-verify configured providers behave the way the
                      registry claims. Makes minimal real API calls.
  routing             Show local per-model outcome measurements (turn success,
                      edit failures, retries, failovers) from the routing ledger.
  resources           List running Elyra instances with age, CPU time, and
                      memory, including language servers and headless browsers,
                      and flag forgotten instances.

models options:
  --provider <name>   Only probe this provider
  --model <pattern>   Only probe models whose id contains this substring
  --max <n>           Maximum total models to probe (default: 10)
  --help, -h          Show this help`);
}

/**
 * `doctor routing` — print ledger measurements plus tier suggestions. Costs
 * come from the built-in registry (no ModelRegistry/models.json is loaded here).
 */
function handleRouting(): boolean {
	const stats = loadStats(getRoutingLedgerPath(), "main");
	const costOf = createCostLookup(toModelCostInfo(getProviders().flatMap((provider) => getModels(provider))));
	const pins = SettingsManager.create(process.cwd()).getSmartRoutingModels();
	console.log(formatStatsReport(stats));
	console.log();
	console.log(formatRecommendations(recommendTierModels(stats, pins, costOf)));
	return true;
}

/** Build a provider -> registry model ids map from the model registry. */
function buildProviderModelIds(): Record<string, string[]> {
	const map: Record<string, string[]> = {};
	for (const provider of getProviders()) {
		map[provider] = getModels(provider as KnownProvider).map((m) => m.id);
	}
	return map;
}

/**
 * `doctor preflight` — offline check that every default model still resolves
 * against the registry (catches defaults pointing at removed model ids before
 * they fail at runtime), plus freshness nudges for defaults with newer siblings.
 */
function handlePreflight(): boolean {
	const { problems, freshness } = checkDefaults(defaultModelPerProvider, buildProviderModelIds());

	if (problems.length === 0) {
		console.log(chalk.green("✓ All default models resolve against the registry."));
	} else {
		console.log(chalk.red(`✗ ${problems.length} default model reference(s) do not resolve:`));
		for (const p of problems) {
			console.log(chalk.red(`  - ${p.message}`));
		}
	}

	if (freshness.length > 0) {
		console.log();
		console.log(chalk.yellow("Newer models available for configured defaults:"));
		for (const f of freshness) {
			console.log(chalk.yellow(`  - ${f.provider}: ${f.currentId} → ${f.newer.join(", ")}`));
		}
	}

	if (problems.length > 0) {
		process.exitCode = 1;
	}
	return true;
}

export async function handleDoctorCommand(args: string[]): Promise<boolean> {
	if (args[0] !== "doctor") {
		return false;
	}

	const sub = args[1];
	if (sub === "--help" || sub === "-h" || sub === undefined) {
		printDoctorHelp();
		return true;
	}

	if (sub === "preflight") {
		return handlePreflight();
	}

	if (sub === "routing") {
		return handleRouting();
	}

	if (sub === "resources") {
		if (process.platform === "win32") {
			console.log("doctor resources is not supported on Windows yet.");
			return true;
		}
		const result = await execCommand("ps", ["axo", "pid=,ppid=,etime=,time=,rss=,command="], process.cwd(), {
			timeout: 10_000,
		});
		if (result.code !== 0) {
			console.error(chalk.red(`Could not inspect processes: ps exited with ${result.code}`));
			process.exitCode = 1;
			return true;
		}
		const instances = findElyraInstances(parsePsOutput(result.stdout), process.pid);
		console.log(formatResourceReport(instances));
		return true;
	}

	if (sub !== "models") {
		console.error(chalk.red(`Unknown doctor command: ${sub}`));
		printDoctorHelp();
		process.exitCode = 1;
		return true;
	}

	let provider: string | undefined;
	let modelPattern: string | undefined;
	let maxModels: number | undefined;

	for (let i = 2; i < args.length; i++) {
		const arg = args[i];
		if (arg === "--provider" && i + 1 < args.length) {
			provider = args[++i];
		} else if (arg === "--model" && i + 1 < args.length) {
			modelPattern = args[++i];
		} else if (arg === "--max" && i + 1 < args.length) {
			maxModels = Number.parseInt(args[++i], 10);
		} else if (arg === "--help" || arg === "-h") {
			printDoctorHelp();
			return true;
		} else {
			console.error(chalk.red(`Unknown option: ${arg}`));
			process.exitCode = 1;
			return true;
		}
	}

	const auth = AuthStorage.create();
	console.log(chalk.dim("Probing configured providers (minimal real API calls)..."));

	const report = await runModelProbes({
		provider,
		modelPattern,
		maxModels,
		// Prefer stored credentials (auth.json), fall back to env vars.
		resolveApiKey: async (p) => (await auth.getApiKey(p)) ?? getEnvApiKey(p),
	});

	console.log();
	console.log(formatProbeReport(report));

	const failed = report.probed.filter((r) => !r.ok).length;
	const mismatches = report.probed.reduce((sum, r) => sum + r.mismatches.length, 0);
	if (failed > 0 || mismatches > 0) {
		process.exitCode = 1;
	}
	return true;
}
